import { moduleReducersToWriteToStorage } from "../modules/reducers";
import { StorageKeys } from "../utils/storage/storage";
import { getGroupDeviceLinksForPastTwoDaysAndModifiedEntities } from "../utils/groupLinkedDevice/groupLinkedDeviceHelper";
import { getTripsForLastTwoDays } from "../utils/trip/tripHelper";
import { getTripAnnotationsForPastTwoDaysAndModifiedEntities } from "../utils/tripCategorization/tripAnnotationHelper";
import { ActiveUserActionTypes } from "./active/types";
import { CacheActionTypes } from "./cache/types";
import { DataActionTypes } from "./data/types";
import { DailyOdometerActionTypes } from "./dailyOdometer/types";
import { DeviceDefectListActionTypes, TrailerDefectListActionTypes } from "./defectLists/types";
import { DeviceLinkActionTypes } from "./deviceLinks/types";
import { DriverChangesActionTypes } from "./driverChanges/types";
import { DriverSeatUserActionTypes } from "./driverSeat/types";
import { DriveAPICredentialsActionTypes } from "./driveAPICredentials/types";
import { DvirLogActionTypes } from "./dvirLogs/types";
import { FeedVersionActionTypes } from "./feedVersions/types";
import { GroupLinkedDeviceActionTypes } from "./groupLinkedDevice/types";
import { HOSAnnotationActionTypes } from "./hosAnnotations/types";
import { SystemHosLogActionTypes, UserHosLogActionTypes, HistoricalDayActionTypes } from "./hosLogs/types";
import { IamCredentialsActionTypes } from "./iamCredentials/types";
import { MessageActionTypes } from "./messages/types";
import { OpenCabQueueActionTypes } from "./openCabQueue/types";
import { OpenCabUsersActionTypes } from "./openCabUsers/types";
import { OpenCabWorkflowActionTypes } from "./openCabWorkflow/types";
import type { IGlobalReducer } from "./reducers";
import { RouteActionTypes } from "./routes/types";
import { ShipmentActionTypes } from "./shipment/types";
import { SuspiciousLogsSentActionTypes } from "./suspiciousLogsSent/types";
import { SystemSettingsActionTypes } from "./systemSettings/types";
import { TimeZoneInfoActionTypes } from "./timeZoneInfo/types";
import { TrailerAttachmentActionTypes } from "./trailerAttachments/types";
import { TranslationActionTypes } from "./translation/types";
import { TripAnnotationActionTypes } from "./tripAnnotations/types";
import { TripActionTypes } from "./trips/types";
import { UserActionTypes } from "./users/types";
import { UserHosRuleSetActionTypes } from "./userHosRuleSet/types";
import { VehicleHistoryActionTypes } from "./vehicleHistory/types";
import { ErrorActionTypes } from "./error/types";

export type DriveReducerNames = keyof IGlobalReducer | keyof typeof moduleReducersToWriteToStorage;

export interface ReducerPersistenceConfig {
    types: { [actionType: string]: string };
    isSecureStorage?: boolean;
    callbackFn?: (stateSlice: any) => any;
}

export type ReducerPersistenceConfigMap = {
    [key in DriveReducerNames]: ReducerPersistenceConfig;
};

export const reducersToWriteToStorage: ReducerPersistenceConfigMap = {
    "activeUsers": { types: ActiveUserActionTypes },
    "cache": { types: CacheActionTypes },
    "data": { types: DataActionTypes },
    "driveAPICredentials": { types: DriveAPICredentialsActionTypes, isSecureStorage: true },
    "users": { types: UserActionTypes },
    "userHosLogs": { types: UserHosLogActionTypes },
    "systemHosLogs": { types: SystemHosLogActionTypes },
    "userHosRuleSets": { types: UserHosRuleSetActionTypes },
    "dvirLogs": { types: DvirLogActionTypes },
    "trips": { types: TripActionTypes, callbackFn: getTripsForLastTwoDays },
    "tripAnnotations": { types: TripAnnotationActionTypes, callbackFn: getTripAnnotationsForPastTwoDaysAndModifiedEntities },
    "groupLinkedDevices": { types: GroupLinkedDeviceActionTypes, callbackFn: getGroupDeviceLinksForPastTwoDaysAndModifiedEntities },
    "feedVersions": { types: FeedVersionActionTypes },
    "timeZoneInfo": { types: TimeZoneInfoActionTypes },
    "dailyOdometer": { types: DailyOdometerActionTypes },
    "messages": { types: MessageActionTypes },
    // A WebView-to-native sync signal; never persist it as an entity cache.
    "messageRevision": { types: {} },
    "driverChanges": { types: DriverChangesActionTypes },
    "deviceDefectLists": { types: DeviceDefectListActionTypes },
    "trailerDefectLists": { types: TrailerDefectListActionTypes },
    "hosAnnotations": { types: HOSAnnotationActionTypes },
    "historicalDayTracker": { types: HistoricalDayActionTypes },
    "routes": { types: RouteActionTypes },
    "trailerAttachments": { types: TrailerAttachmentActionTypes },
    "shipments": { types: ShipmentActionTypes },
    "openCabUsers": { types: OpenCabUsersActionTypes },
    "openCabWorkflow": { types: OpenCabWorkflowActionTypes },
    "openCabQueue": { types: OpenCabQueueActionTypes },
    "iamCredentials": { types: IamCredentialsActionTypes, isSecureStorage: true },
    "systemSettings": { types: SystemSettingsActionTypes },
    "deviceLinks": { types: DeviceLinkActionTypes },
    "vehicleHistory": { types: VehicleHistoryActionTypes },
    "translations": { types: TranslationActionTypes },
    "driverSeatUser": { types: DriverSeatUserActionTypes },
    "error": { types: ErrorActionTypes },
    "suspiciousLogsSent": { types: SuspiciousLogsSentActionTypes },
    "mobile": { types: {} }, // No actions to store
    "scheduleStatus": { types: {} }, // No actions to store
    "tripsTodayCount": { types: {} }, // Derived from trips; stale across days, so not stored
    "dvirRepairNeeded": { types: {} }, // Derived from dvirLogs; re-seeded from them in preload
    "deviceMovement": { types: {} }, // No actions to store
    "hosRuleResults": { types: {} }, // No actions to store
    ...moduleReducersToWriteToStorage
};

export function isSecureStorageReducer(reducerKey: DriveReducerNames): boolean {
    return reducersToWriteToStorage[reducerKey].isSecureStorage === true;
}

/**
 * Reducers that are written to BOTH localStorage (sync) and IndexedDB (async) by the middleware.
 * Used by both middleware (to decide where to write) and preload (to decide where to read first).
 * localStorage is always at least as fresh as IndexedDB for these keys, since the sync write
 * completes before any page navigation that interrupts the async IndexedDB transaction.
 * Reducers flagged isSecureStorage (the two credential keys) go to native secure storage instead
 * whenever the host provides it (e.g. the RN app, MMKV-backed), so this set only affects them in
 * browsers and hosts without it. The others are written to the web document's localStorage on
 * every host, including the RN WebView.
 *
 * Add-ins run in that same document and read ACTIVE_USERS_KEY straight from localStorage (`geotabDrive_@store_activeUsers`):
 * Border Crossings (d2go) to find the logged-in team for its ruleset switch, and Malfunctions report
 * (d2go) to detect logout. Do not remove it before those add-ins have a sanctioned replacement
 * (lifecycle events, MYG-148937) and internal access is closed (MYG-148943). See DRPJPD-194 and
 * tests/app/services/addins/addinLocalStorageContractTest.ts.
 * @deprecated Remove once those compatibility + test cases are removed, and ACTIVE_USERS_KEY only after the above.
 */
export const REDUCER_KEYS_STILL_IN_LOCALSTORAGE: ReadonlySet<string> = new Set([
    StorageKeys.ACTIVE_USERS_KEY,           // storage.ts::getActiveUserNames
    StorageKeys.USERS_KEY,                  // storage.ts::getUsersObject
    StorageKeys.IAM_CREDENTIALS_KEY,        // iamStore.ts::checkIfLocalStorageHasBeenUpdated
    StorageKeys.DRIVE_API_CREDENTIALS_KEY   // preload.ts: fallback when IndexedDB write races with IAM redirect
]);
